import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Icon from "../AppIcon";
import LanguageSwitcher from "./LanguageSwitcher";
import { useAuth } from "../../lib/AuthProvider";
import { useIntegrationAccess } from "../../hooks/useIntegrationAccess";

const ROLE_LABELS = { owner: "Owner", admin: "Admin", agent: "Agente", viewer: "Viewer" };
const cx = (...classes) => classes.filter(Boolean).join(" ");

const NavigationSidebar = ({ isCollapsed, onToggleCollapse, isMobileOpen, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { logout, profile, tenant, tenants, setActiveTenant, workspaceLoading } = useAuth();
  const integrationAccess = useIntegrationAccess();
  const role = profile?.role || "viewer";
  const canConfigure = role === "owner" || role === "admin";
  const canSeeAnalytics = canConfigure || role === "viewer";
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [collapsedSections, setCollapsedSections] = useState({});

  const sections = useMemo(() => [
    { key: "overview", label: t("nav.sections.overview"), items: [
      { label: t("nav.dashboard"), path: "/app/overview", icon: "LayoutDashboard" },
    ] },
    { key: "operation", label: t("nav.sections.operation"), items: [
      { label: t("nav.agentInbox"), path: "/app/inbox", icon: "MessagesSquare" },
      { label: t("nav.contacts"), path: "/app/contacts", icon: "UsersRound" },
      { label: t("nav.campaigns"), path: "/app/campaigns", icon: "Megaphone" },
    ] },
    ...(canConfigure ? [{ key: "intelligence", label: t("nav.sections.intelligence"), items: [
      { label: t("nav.aiAssistant"), path: "/app/assistant", icon: "Sparkles" },
      { label: t("nav.automation"), path: "/app/automations", icon: "Workflow" },
      { label: t("nav.templates"), path: "/app/templates", icon: "LayoutTemplate" },
    ] }] : []),
    ...(canSeeAnalytics ? [{ key: "analysis", label: t("nav.sections.analysis"), items: [
      { label: t("nav.analytics"), icon: "ChartNoAxesCombined", soon: true },
      { label: t("nav.messagesLog"), path: "/app/messages", icon: "ScrollText" },
    ] }] : []),
    ...(canConfigure ? [{ key: "settings", label: t("nav.sections.settings"), items: [
      { label: t("nav.channels"), path: "/app/channels", icon: "RadioTower" },
      { label: t("nav.team"), path: "/app/team", icon: "UserRoundCog" },
      // En "Ajustes" y no en "Operación": la etiqueta se ASIGNA en la bandeja, pero el catálogo lo
      // administra owner/admin, y esta sección ya está gateada por ese mismo rol.
      { label: t("nav.tags"), path: "/app/tags", icon: "Tags" },
      ...(integrationAccess.available ? [{ label: t("nav.integrations"), path: "/app/integrations", icon: "PlugZap" }] : []),
      ...(role === "owner" ? [{ label: t("nav.billing"), path: "/app/billing", icon: "CreditCard" }] : []),
    ] }] : []),
  ], [canConfigure, canSeeAnalytics, integrationAccess.available, role, t]);

  const visibleSections = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    if (!normalized) return sections;
    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => item.label.toLocaleLowerCase().includes(normalized)),
      }))
      .filter((section) => section.items.length);
  }, [query, sections]);

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const navigateTo = (item) => {
    if (!item.path || item.soon) return;
    navigate(item.path);
    onMobileClose?.();
  };

  const selectWorkspace = async (tenantId) => {
    const result = await setActiveTenant(tenantId);
    if (result.ok) {
      setWorkspaceOpen(false);
      navigate("/app/overview");
      onMobileClose?.();
    }
  };

  const NavItem = ({ item, compact = false }) => {
    const active = item.path && location.pathname === item.path;
    return (
      <button
        type="button"
        onClick={() => navigateTo(item)}
        disabled={item.soon}
        className={cx(
          "group/item relative flex min-h-10 w-full items-center rounded-lg border outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#5146e5]/25",
          compact ? "justify-center px-0" : "gap-3 px-3 py-2",
          active
            ? "border-[#dcd9fb] bg-white text-[#101828] shadow-[0_1px_2px_rgba(16,24,40,.05)]"
            : item.soon
              ? "cursor-default border-transparent text-slate-400"
              : "border-transparent text-slate-600 hover:bg-[#ecefeb] hover:text-[#101828]",
        )}
        title={compact ? item.label : undefined}
      >
        <Icon name={item.icon} size={18} className={cx("shrink-0", active ? "text-[#5146e5]" : "text-slate-400 group-hover/item:text-slate-600")} />
        {!compact && <>
          <span className="min-w-0 flex-1 truncate text-left text-[13px] font-semibold tracking-[-0.01em]">{item.label}</span>
          {item.soon && <span className="rounded-md bg-slate-200/70 px-1.5 py-0.5 text-[8px] font-bold text-slate-500">{t("nav.soon")}</span>}
        </>}
      </button>
    );
  };

  const SidebarBody = ({ compact = false, mobile = false }) => (
    <div className="relative flex h-full flex-col overflow-hidden border-r border-[#dfe3e1] bg-[#f6f7f5] text-slate-700">
      <div className={cx("flex h-[72px] shrink-0 items-center border-b border-[#e2e5e3]", compact ? "justify-center px-2" : "px-4")}>
        <div className="flex min-w-0 items-center gap-3">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] border border-[#dfe3e1] bg-white">
            <img src="/brand/matchbot-logo-192.png" alt="" className="h-7 w-7 object-contain" />
          </div>
          {!compact && <div className="min-w-0">
            <p className="truncate text-[15px] font-extrabold tracking-[-0.035em] text-[#101828]">Match<span className="text-[#5146e5]">Bot</span></p>
            <p className="truncate text-[10px] font-medium text-slate-500">{t("nav.commandCenter")}</p>
          </div>}
        </div>
        {mobile && <button type="button" onClick={onMobileClose} className="ml-auto grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-200/70 hover:text-slate-900" aria-label={t("actions.close")}><Icon name="X" size={19} /></button>}
      </div>

      {!compact && <div className="relative mx-3 mt-3">
        <button type="button" onClick={() => setWorkspaceOpen((value) => !value)} disabled={workspaceLoading} className="flex w-full items-center gap-3 rounded-xl border border-[#dfe3e1] bg-white p-3 text-left shadow-[0_1px_2px_rgba(16,24,40,.03)] disabled:opacity-60" aria-expanded={workspaceOpen}>
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#eeedff] text-xs font-extrabold text-[#4940c9]">{(tenant?.name || "W").charAt(0).toUpperCase()}</div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-bold text-[#101828]">{tenant?.name || t("nav.workspace")}</p>
            <p className="mt-0.5 text-[9px] font-semibold text-slate-500">{ROLE_LABELS[role] || role}</p>
          </div>
          <Icon name={workspaceLoading ? "LoaderCircle" : "ChevronsUpDown"} size={14} className={workspaceLoading ? "animate-spin text-slate-400" : "text-slate-400"} />
        </button>
        {workspaceOpen && <div className="absolute inset-x-0 top-[calc(100%+6px)] z-50 overflow-hidden rounded-xl border border-[#dfe3e1] bg-white shadow-[0_14px_34px_rgba(16,24,40,.14)]">
          <p className="border-b border-slate-100 px-3 py-2 text-[10px] font-semibold text-slate-500">{t("nav.chooseWorkspace")}</p>
          <div className="max-h-52 overflow-y-auto p-1.5">
            {tenants.map((membership) => {
              const active = membership.tenant_id === tenant?.id;
              return <button key={membership.tenant_id} type="button" onClick={() => selectWorkspace(membership.tenant_id)} className={cx("flex min-h-11 w-full items-center gap-2 rounded-lg px-2.5 text-left", active ? "bg-[#eeedff] text-[#101828]" : "text-slate-600 hover:bg-slate-50 hover:text-[#101828]")}>
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-slate-100 text-[10px] font-extrabold">{(membership.tenants?.name || "W").charAt(0).toUpperCase()}</span>
                <span className="min-w-0 flex-1"><span className="block truncate text-[11px] font-bold">{membership.tenants?.name || t("nav.workspace")}</span><span className="block text-[9px] text-slate-400">{ROLE_LABELS[membership.role] || membership.role}</span></span>
                {active && <Icon name="Check" size={13} className="text-[#5146e5]" />}
              </button>;
            })}
          </div>
        </div>}
      </div>}

      {!compact && <div className="mx-3 mt-3 flex h-10 items-center gap-2 rounded-lg border border-[#e0e4e1] bg-[#eef0ed] px-3 text-slate-500 transition-[border-color,background-color,box-shadow] focus-within:border-[#aaa4f1] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#5146e5]/10">
        <Icon name="Search" size={15} className="shrink-0" />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("nav.search")} className="min-w-0 flex-1 border-0 bg-transparent p-0 text-xs text-[#101828] shadow-none outline-none ring-0 placeholder:text-slate-400 focus:border-0 focus:outline-none focus:ring-0" />
      </div>}

      <nav className={cx("relative flex-1 overflow-y-auto overscroll-contain py-3", compact ? "space-y-1 px-2" : "px-3")} aria-label={t("nav.workspace")}>
        {visibleSections.map((section, index) => {
          const expanded = compact || query || !collapsedSections[section.key];
          return <div key={section.key} className={cx(index && "mt-4", compact && index && "border-t border-[#e2e5e3] pt-2")}>
            {!compact && <button
              type="button"
              onClick={() => setCollapsedSections((value) => ({ ...value, [section.key]: !value[section.key] }))}
              className="group flex min-h-7 w-full items-center px-2 text-left"
              aria-expanded={!!expanded}
            >
              <span className="min-w-0 flex-1 text-[11px] font-semibold text-slate-500 group-hover:text-slate-700">{section.label}</span>
              {!query && <Icon name="ChevronDown" size={13} className={cx("text-slate-400 transition-transform", expanded && "rotate-180")} />}
            </button>}
            {expanded && <div className="mt-1 space-y-0.5">
              {section.items.map((item) => <NavItem key={`${section.key}-${item.path || item.label}`} item={item} compact={compact} />)}
            </div>}
          </div>;
        })}
        {!visibleSections.length && !compact && <p className="px-3 py-8 text-center text-xs text-slate-400">{t("nav.noResults")}</p>}

        {profile?.is_superadmin && <div className="mt-5 border-t border-[#e2e5e3] pt-4">
          {!compact && <p className="mb-1 px-2 text-[11px] font-semibold text-slate-500">{t("nav.platform")}</p>}
          <NavItem item={{ label: t("nav.admin"), path: "/app/admin", icon: "ShieldCheck" }} compact={compact} />
          <NavItem item={{ label: t("nav.aiCapabilities"), path: "/app/admin/capabilities", icon: "BrainCircuit" }} compact={compact} />
          <NavItem item={{ label: t("nav.integrations"), path: "/app/admin/integrations", icon: "PlugZap" }} compact={compact} />
        </div>}

        {canConfigure && !compact && !query && <div className="mt-5 rounded-xl border border-[#dddafa] bg-[#efeeff] p-3.5">
          <div className="flex items-center gap-2 text-[#4940c9]"><Icon name="ListChecks" size={16} /><p className="text-[11px] font-bold">{t("nav.setupTitle")}</p></div>
          <p className="mt-2 text-[10px] leading-relaxed text-slate-600">{t("nav.setupCopy")}</p>
          <button type="button" onClick={() => navigateTo({ path: "/app/channels" })} className="mt-3 inline-flex min-h-8 items-center text-[10px] font-bold text-[#4138b8] hover:underline">{t("nav.setupAction")}<Icon name="ArrowRight" size={13} className="ml-1.5" /></button>
        </div>}
      </nav>

      <div className={cx("relative shrink-0 border-t border-[#e2e5e3] bg-[#f3f5f2]", compact ? "p-2" : "p-3")}>
        {!compact && <div className="mb-2 flex justify-start"><LanguageSwitcher variant="light" /></div>}
        <button type="button" onClick={handleLogout} className={cx("flex min-h-10 w-full items-center rounded-lg text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-700", compact ? "justify-center" : "gap-3 px-3")} title={compact ? t("nav.signOut") : undefined}>
          <Icon name="LogOut" size={17} />{!compact && <span className="text-xs font-semibold">{t("nav.signOut")}</span>}
        </button>
      </div>

      {!mobile && <button type="button" onClick={onToggleCollapse} className="absolute -right-3 top-[22px] z-50 hidden h-7 w-7 items-center justify-center rounded-lg border border-[#d9ddda] bg-white text-slate-500 shadow-sm transition hover:text-[#5146e5] md:flex" title={compact ? t("actions.expand") : t("actions.collapse")}><Icon name={compact ? "ChevronRight" : "ChevronLeft"} size={14} /></button>}
    </div>
  );

  return <>
    <aside className={cx("fixed inset-y-0 left-0 z-50 hidden transition-[width] duration-300 md:block", isCollapsed ? "w-[72px]" : "w-[272px]")}><SidebarBody compact={isCollapsed} /></aside>
    <aside className={cx("fixed inset-y-0 left-0 z-50 w-[min(88vw,340px)] transform transition-transform duration-300 md:hidden", isMobileOpen ? "translate-x-0" : "-translate-x-full")}><SidebarBody mobile /></aside>
  </>;
};

export default NavigationSidebar;
